/**
 * == Controller: ContentSpaceController
 *
 * Coordinates the conversational interface within the Content Space view.
 * Handles chat submission, history pagination, clipboard interactions, and
 * keeps the transcript scrolled to the latest response.
 *
 * === Responsibilities:
 * - Persist chat identifiers and honour system prompt overrides.
 * - Drive Turbo-powered fetches for ask/reset/settings routes.
 * - Manage Mutation/Resize observers to auto-scroll the chat window.
 * - Provide convenience actions for loading and copying historic answers.
 */
import PlatformBaseController from "./platform_base_controller";

// Main controller
export default class extends PlatformBaseController {
    static targets = ["chatEditor", "chatId", "systemPromptEditor", "answerContent"];

    static values = {
        historyChatId: { type: String, default: undefined },
    };

    initialize() {
        this.page = 1;
    }

    connect() {
        super.connect();

        this.boundQueueScroll = () => this.queueScroll();

        document.addEventListener("turbo:load", this.boundQueueScroll);
        document.addEventListener("turbo:render", this.boundQueueScroll);
        document.addEventListener("turbo:frame-load", this.boundQueueScroll);
        document.addEventListener("turbo:before-stream-render", this.boundQueueScroll);

        const el = this.contentEl;

        this.mo = new MutationObserver(this.boundQueueScroll);
        if (el) this.mo.observe(el, { childList: true, subtree: true, characterData: true });

        this.ro = new ResizeObserver(this.boundQueueScroll);
        if (el) this.ro.observe(el);

        this.queueScroll();
    }

    disconnect() {
        this.mo?.disconnect();
        this.ro?.disconnect();
        document.removeEventListener("turbo:load", this.boundQueueScroll);
        document.removeEventListener("turbo:render", this.boundQueueScroll);
        document.removeEventListener("turbo:frame-load", this.boundQueueScroll);
        document.removeEventListener("turbo:before-stream-render", this.boundQueueScroll);
        super.disconnect();
    }

    get contentEl() {
        if (this.hasContentTarget) return this.contentTarget;
        return this.element.querySelector(".chat_content");
    }

    // ---------- Scrolling ----------
    queueScroll() {
        cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => this.scrollToBottom());
    }

    scrollToBottom() {
        const el = this.contentEl;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
    }

    // ---------- Submit (uses original textarea) ----------
    ask(event) {
        event.preventDefault();

        const chatMessage = this.chatEditorTarget.value.trim();
        const paramsChatId = this.chatIdTarget.value.trim();
        const chatId = this.getChatId() || paramsChatId;

        if (chatId !== undefined || chatId == "") {
            this.setPersistentFilter("chat_id", chatId);
        }

        const formData = new FormData();
        formData.append("chat_id", chatId);

        const token = document.querySelector('meta[name="csrf-token"]')?.content;

      if (document.getElementById('history_block').style.display == 'block')
        document.getElementById('show_history').click();

        // Clear textarea UI immediately
        this.chatEditorTarget.value = "";

        if (!chatMessage) {
            this.fetchAction("/content_space/empty_question", {
                method: "POST",
                headers: { "X-CSRF-Token": token },
                body: formData
            });
            return;
        }

        formData.append("chat_message", chatMessage);
        this.fetchAction("/content_space/ask", {
            method: "POST",
            headers: { "X-CSRF-Token": token },
            body: formData
        });
    }

    // Keep the textarea newline behavior
    insertNewline(event) {
        event.preventDefault();
        const el = event.target;
        const start = el.selectionStart;
        const end = el.selectionEnd;
        el.value = el.value.slice(0, start) + "\n" + el.value.slice(end);
        el.selectionStart = el.selectionEnd = start + 1;
    }

    // ---------- Other actions ----------
    async resetChat(event) {
        event.preventDefault();

        if (!confirm("Do you wish to begin a new session?")) return;

        const chatId = this.getChatId();

        const formData = new FormData();
        formData.append("chat_id", chatId);

        const token = document.querySelector('meta[name="csrf-token"]')?.content;

        await this.fetchAction("/content_space", {
            method: "delete",
            headers: { "X-CSRF-Token": token },
            body: formData,
            onSuccess: () => window.location.reload()
        });
    }

    saveSettings(event) {
        event.preventDefault();

        const systemPrompt = this.systemPromptEditorTarget.value.trim();

        const formData = new FormData();
        formData.append("chat_system_prompt", systemPrompt);

        const token = document.querySelector('meta[name="csrf-token"]')?.content;

        this.fetchAction("/content_space/apply_settings", {
            method: "post",
            headers: { "X-CSRF-Token": token },
            body: formData,
            onSuccess: () => window.location.reload()
        });
    }

    async resetSettings(event) {
        event.preventDefault();

        if (!confirm("Do you wish to reset system prompt to default?")) return;

        const token = document.querySelector('meta[name="csrf-token"]')?.content;

        await this.fetchAction("/content_space/reset_settings", {
            method: "delete",
            headers: { "X-CSRF-Token": token },
            onSuccess: () => window.location.reload()
        });
    }

    loadMoreHistory(event) {
        event.preventDefault();
        this.page++;

        const token = document.querySelector('meta[name="csrf-token"]').content;

        const requestParams = new URLSearchParams(window.location.search);
        const search = requestParams.get("search");

        const params = new URLSearchParams();
        params.set("page", this.page);
        if (search) params.append("search", search);

        this.fetchAction(`/content_space/load_more_history?${params.toString()}`, {
            method: "get",
            headers: {
                "X-CSRF-Token": token,
                "Accept": "text/vnd.turbo-stream.html"
            },
            body: null,
            onSuccess: (_responseText) => {},
            onError: (error) => {
                console.error("Failed to load more items:", error);
            }
        });
    }

    loadChatHistory(event) {
        event.preventDefault();

        const currentChatId = this.getChatId();
        const chatId = event.currentTarget.dataset.contentSpaceHistoryChatIdValue;

        const token = document.querySelector('meta[name="csrf-token"]').content;

        this.setPersistentFilter("chat_id", chatId);

        const formData = new FormData();
        formData.append("chat_id", currentChatId);
        formData.append("next_chat_id", chatId);

        this.fetchAction("/content_space/register_chat", {
            method: "post",
            headers: { "X-CSRF-Token": token },
            body: formData,
            onSuccess: () => window.location.reload(),
            accept: "text/html"
        });
    }

    searchHistory(event) {
        event.preventDefault();
        const form = event.target.closest("form") || event.target.form || this.element;

        const token = document.querySelector('meta[name="csrf-token"]').content;

        const search = form.querySelector('[name="search"]')?.value;

        const params = new URLSearchParams();
        this.page = 1;
        params.set("page", this.page);

        if (search) params.append("search", search);

        this.fetchAction(`/content_space/search_history?${params.toString()}`, {
            method: "get",
            headers: { "X-CSRF-Token": token }
        });
    }

    // ---------- Copy answer (keeps HTML formatting) ----------
    async copyAnswer(event) {
        event.preventDefault();
        const block =
            event.currentTarget.closest(".chat_block_a, .chat_block_q") ||
            event.currentTarget.closest(".chat_block");
        const el = block?.querySelector(".chat_block_content_txt");
        if (!el) return;

        const html = el.innerHTML;
        const text = (el.textContent || "")
            .replace(/\u00A0/g, " ")
            .replace(/\r\n/g, "\n")
            .trim();

        try {
            if (navigator.clipboard?.write && window.ClipboardItem) {
                const item = new ClipboardItem({
                    "text/html": new Blob([html], { type: "text/html" }),
                    "text/plain": new Blob([text], { type: "text/plain" })
                });
                await navigator.clipboard.write([item]);
                return;
            }
        } catch (_) {}

        // Legacy fallback
        const temp = document.createElement("div");
        temp.contentEditable = "true";
        temp.style.position = "fixed";
        temp.style.left = "-9999px";
        temp.innerHTML = html;
        document.body.appendChild(temp);
        const range = document.createRange();
        range.selectNodeContents(temp);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        try {
            document.execCommand("copy");
        } finally {
            sel.removeAllRanges();
            document.body.removeChild(temp);
        }
    }

    getChatId() {
        return this.getPersistedFilter(this.element, "chat_id");
    }
}
